/*
 * KZA PDP (ported from the prior Shopify theme)
 * - SS26.2 (spec 2026-09-27 §4): swipe gallery + dots, tap-to-zoom dialog, the "Complete the
 *   look" rail order (driven by the drawer's kza:cart event), and the phone sticky Add to Bag.
 * - Color/size button click -> find matching variant, update hidden id input, ATC label/price,
 *   sold-out state, main image.
 *
 * Product JSON is embedded in the PDP via <script type="application/json" data-lv-product-json>.
 * ES5 on purpose: no arrow functions, let/const or template literals in theme JS.
 */
(function () {
  'use strict';

  var pdp = document.querySelector('[data-lv-pdp]');
  if (!pdp) return;

  /* ---------- Gallery ---------- */
  var mainImg = pdp.querySelector('#lv-main-img');

  function setMainImage(src, srcset) {
    if (!mainImg || !src) return;
    mainImg.setAttribute('src', src);
    if (srcset) mainImg.setAttribute('srcset', srcset);
    else mainImg.removeAttribute('srcset');
  }

  // Scroll the first option still waiting for a choice into view and mark it (.kza-attn); falls
  // back to the size block (a sold-out selection). Shared by the sticky bar and the Add to Bag
  // guard. No toast, no alert: the outlined picker is the whole message.
  function nudgePicker() {
    var blks = [].slice.call(pdp.querySelectorAll('[data-lv-opt-block]'));
    var blk = blks.filter(function (b) { return !b.querySelector('[data-lv-opt-btn][aria-checked="true"]'); })[0]
      || pdp.querySelector('.lv-size-block[data-lv-opt-block]') || blks[0];
    var target = blk || pdp.querySelector('[data-lv-atc]');
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (!blk) return;
    blk.classList.remove('kza-attn');
    void blk.offsetWidth;  // restart the highlight if it is tapped twice
    blk.classList.add('kza-attn');
    var first = blk.querySelector('[data-lv-opt-btn]');
    if (first) first.focus({ preventScroll: true });
  }

  /* ---------- SS26.2 additions (spec §4) ----------
   * Placed ahead of the variant logic because that logic returns early on bad or truncated
   * product JSON, and none of this depends on it. */

  // Gallery: native scroll-snap does the swiping; JS only keeps the dots honest. A dot tap
  // scrolls the track sideways only (scrollTo, not scrollIntoView, which would also jump the
  // page vertically when the gallery is part-way off screen).
  var gal = pdp.querySelector('[data-kza-gallery]');
  var slides = gal ? gal.querySelectorAll('[data-kza-slide]') : [];
  var dots = pdp.querySelectorAll('[data-kza-dots] button');
  if (gal && dots.length && 'IntersectionObserver' in window) {
    var dotIo = new IntersectionObserver(function (es) {
      es.forEach(function (en) {
        if (!en.isIntersecting) return;
        var i = Array.prototype.indexOf.call(slides, en.target);
        dots.forEach(function (d, j) { d.setAttribute('aria-current', j === i ? 'true' : 'false'); });
      });
    }, { root: gal, threshold: 0.6 });
    slides.forEach(function (s) { dotIo.observe(s); });
    dots.forEach(function (d, j) {
      d.addEventListener('click', function () {
        if (!slides[j]) return;
        gal.scrollTo({ left: slides[j].offsetLeft - slides[0].offsetLeft, behavior: 'smooth' });
      });
    });
  }

  // Per-colour galleries: slides/dots carry data-kza-color (from the image alt "<Color> | n"); show only the
  // selected colour's set and start it from its first photo. Untagged slides always show.
  var colorGallery = gal && gal.hasAttribute('data-kza-color-gallery');
  function visibleSlides() { return Array.prototype.filter.call(slides, function (s) { return !s.hidden; }); }
  function filterGalleryByColor(val) {
    if (!colorGallery) return;
    var v = String(val == null ? '' : val).trim().toLowerCase();
    slides.forEach(function (s, i) {
      var c = s.getAttribute('data-kza-color');
      var show = !c || !v || c.trim().toLowerCase() === v;
      s.hidden = !show; if (dots[i]) dots[i].hidden = !show;
    });
    gal.scrollTo({ left: 0 });
    var vis = visibleSlides(); dots.forEach(function (d, j) { d.setAttribute('aria-current', slides[j] === vis[0] ? 'true' : 'false'); });
    if (typeof syncArrows === 'function') syncArrows();
  }
  // Arrows (desktop): one slide per click; hidden at either end. Works off scrollLeft so it stays
  // in step with swipes and dot taps.
  var prevBtn = pdp.querySelector('[data-kza-gal-prev]'), nextBtn = pdp.querySelector('[data-kza-gal-next]');
  if (gal && slides.length > 1 && prevBtn && nextBtn) {
    var step = function () { var vis = visibleSlides(); return (vis[1] && vis[0] ? vis[1].offsetLeft - vis[0].offsetLeft : 0) || gal.clientWidth; };
    var syncArrows = function () {
      var max = gal.scrollWidth - gal.clientWidth;
      prevBtn.disabled = gal.scrollLeft <= 2;
      nextBtn.disabled = gal.scrollLeft >= max - 2;
    };
    prevBtn.addEventListener('click', function () { gal.scrollBy({ left: -step(), behavior: 'smooth' }); });
    nextBtn.addEventListener('click', function () { gal.scrollBy({ left: step(), behavior: 'smooth' }); });
    gal.addEventListener('scroll', syncArrows, { passive: true });
    window.addEventListener('resize', syncArrows);
    syncArrows();
  }

  // Zoom: tap a slide -> full-screen <dialog> with its 1600w image; pinch-zoom is native there.
  // Any tap (or Esc) closes it. The page behind is locked while it is open.
  var zoom = pdp.querySelector('[data-kza-zoom]');
  if (gal && zoom && typeof zoom.showModal === 'function') {
    var zoomImg = zoom.querySelector('img');
    gal.addEventListener('click', function (e) {
      var img = e.target.closest('[data-kza-slide] img');
      if (!img || !zoomImg) return;
      zoomImg.src = img.getAttribute('data-zoom-src') || img.currentSrc || img.src;
      zoomImg.alt = img.alt;
      document.documentElement.style.overflow = 'hidden';
      zoom.showModal();
    });
    zoom.addEventListener('click', function () { zoom.close(); });
    zoom.addEventListener('close', function () { document.documentElement.style.overflow = ''; });
  }

  // "Complete the look" rail order, from the cart drawer's kza:cart event ({ cart, ladder }), which
  // it fires after every render and once on page load (layout/theme.liquid loads this file first
  // so the listener exists by then). (The PDP gift nudge that also lived here was removed
  // 2026-09-28 — Gibbs swapped it for the trust row.)
  var look = pdp.querySelector('[data-kza-look]');
  document.addEventListener('kza:cart', function (e) {
    var l = e.detail && e.detail.ladder;
    var G = window.KZAGifts;
    if (!l || !G) return;
    if (look) {
      var cards = Array.prototype.slice.call(look.querySelectorAll('[data-price-cents]'))
        .map(function (el) { return { el: el, priceCents: +el.getAttribute('data-price-cents') }; });
      var ordered = G.bridgeOrder(cards, l.next ? l.next.remaining : null);
      // Only touch the DOM when the order really changes: re-appending a card blurs a focused
      // Add button and resets nothing useful.
      var same = ordered.every(function (c, i) { return c.el === cards[i].el; });
      if (!same) {
        ordered.forEach(function (c) { look.appendChild(c.el); });
        look.scrollLeft = 0;
      }
    }
  });

  // Desktop sticky info column (kza-pdp.css caps its `top` with this): a column taller than the
  // viewport must scroll until its bottom shows, then stick. ResizeObserver keeps the height
  // current as accordions open and close. Harmless on phones, where the column isn't sticky.
  var details = pdp.querySelector('.lv-details');
  if (details && 'ResizeObserver' in window) {
    new ResizeObserver(function () {
      details.style.setProperty('--kza-details-h', details.offsetHeight + 'px');
    }).observe(details);
  }

  // Sticky Add to Bag (phones): visible only while the real button is scrolled off the top.
  // With no valid selection (the real button is disabled: sold-out or unmatched size) a tap
  // scrolls to the picker and briefly marks it, rather than failing silently.
  var sticky = pdp.querySelector('[data-kza-sticky-atc]');
  var mainAtc = pdp.querySelector('[data-lv-atc]');
  if (sticky && mainAtc && 'IntersectionObserver' in window) {
    var stickyBtn = sticky.querySelector('[data-kza-sticky-btn]');
    var stickySize = sticky.querySelector('[data-kza-sticky-size]');
    var stickyPrice = sticky.querySelector('[data-kza-sticky-price]');
    // The root margin stretches the observed area far below the viewport, so "not intersecting"
    // means exactly "scrolled off the top". A plain observer only fires on a state change, and a
    // fling or jump that carries the button from below the fold straight past the top is
    // not-intersecting both times: the bar would never appear (caught on the preview).
    new IntersectionObserver(function (es) {
      var off = !es[0].isIntersecting && es[0].boundingClientRect.bottom <= 0;
      sticky.hidden = !off;
      sticky.setAttribute('aria-hidden', off ? 'false' : 'true');
    }, { rootMargin: '0px 0px 100000px 0px' }).observe(mainAtc);
    if (stickyBtn) stickyBtn.addEventListener('click', function () {
      if (mainAtc.disabled) { nudgePicker(); return; }
      // No size chosen yet? The real button's own click guard (below) scrolls to the picker.
      mainAtc.click();
    });
    var syncSticky = function () {
      var sz = pdp.querySelector('.lv-size-block [data-lv-selected-value]');
      var pr = pdp.querySelector('[data-lv-price]');
      if (stickySize) stickySize.textContent = sz ? sz.textContent.trim() : '';
      if (stickyPrice && pr) stickyPrice.textContent = pr.textContent.trim();
    };
    pdp.addEventListener('click', function (e) { if (e.target.closest('[data-lv-opt-btn]')) setTimeout(syncSticky, 0); });
    syncSticky();
  }

  /* ---------- Variant selection ----------
   * 2026-08-14 REWRITE. Options are matched by INDEX, never by name.
   *
   * The previous version resolved a `colorIdx` by looking up the literal strings
   * "Color"/"Colour" in product.options. BOM BERLANDS names its options "Shoe size"
   * and "Shoe strings color", so colorIdx came back -1, findVariant() matched on
   * option1 alone, and every one of its 48 size x lace-colour combinations rendered
   * "Sold Out" on in-stock inventory ($500 SKU, 42+ variants unbuyable).
   *
   * Option names are now presentation only. Everything below keys off the option's
   * position, works for N options, and compares values as STRINGS so "8", "08",
   * "8.0" and "9.5" stay distinct and never get numerically coerced.
   */
  var productJsonEl = pdp.querySelector('[data-lv-product-json]');
  if (!productJsonEl) return;

  var product;
  try { product = JSON.parse(productJsonEl.textContent); }
  catch (e) { return; }
  if (!product || !product.variants || !product.variants.length) return;

  var pdpForm = pdp.querySelector('[data-lv-pdp-form]');
  var hiddenId = pdp.querySelector('[data-lv-variant-id]');
  var atcBtn = pdp.querySelector('[data-lv-atc]');
  var atcLabel = pdp.querySelector('[data-lv-atc-label]');
  var priceEl = pdp.querySelector('[data-lv-price]');
  var stockEl = pdp.querySelector('[data-lv-stock-message]');
  // Spec 2026-09-27 §4.2: a real low-stock line only when 3 or fewer are left in the chosen size.
  var LOW_STOCK_THRESHOLD = 3;

  /* Liquid caps product.variants at 250. Past that the serialized set is INCOMPLETE:
   * a ?variant= deep link outside it would miss below, fall back to variants[0], and
   * repaint labels/price for the wrong variant while the hidden id still held the
   * deep-linked one -- with ATC enabled. So fail CLOSED: do not initialize variant
   * state at all. Liquid's server-rendered hidden id + ATC state are correct for the
   * landed variant, so that variant stays purchasable; only in-page switching is lost.
   * There is no native <select> to fall back to -- this form is buttons + hidden id. */
  if (product.variants_truncated) {
    console.error('[lv-pdp] product.variants truncated at 250 (' + product.variants.length +
      ' serialized). Variant switching disabled -- needs a Section Rendering refactor.');
    pdp.querySelectorAll('[data-lv-opt-btn], [data-lv-color], [data-lv-size]')
      .forEach(function (b) { b.setAttribute('disabled', ''); });
    var warn = document.createElement('p');
    warn.className = 'lv-stock-message lv-stock-soldout';
    warn.textContent = 'Too many variants to switch here — use the size/colour links.';
    var firstBlock = pdp.querySelector('[data-lv-opt-block], .lv-color-block, .lv-size-block');
    if (firstBlock && firstBlock.parentNode) firstBlock.parentNode.insertBefore(warn, firstBlock);
    return;
  }

  function sval(x) { return x === null || typeof x === 'undefined' ? null : String(x); }

  /* Option blocks, in DOM order. Prefer the index-driven markup; fall back to the
   * legacy classes so a half-deployed theme (new JS + old Liquid) still works. */
  var blocks = [].slice.call(pdp.querySelectorAll('[data-lv-opt-block]'));
  if (!blocks.length) {
    blocks = [].slice.call(pdp.querySelectorAll('.lv-color-block, .lv-size-block'));
  }
  // Option chips only. A bare 'button' query also caught the "Size guide" link, which lives in the
  // size block's label row: availability then renamed it "Size null (sold out)" and a tap on it
  // cleared the size selection.
  var optBlocks = blocks.map(function (el, i) {
    var declared = el.getAttribute('data-lv-opt-index');
    return {
      el: el,
      index: declared === null ? i : parseInt(declared, 10),
      label: el.querySelector('[data-lv-selected-value], [data-lv-selected-color], [data-lv-selected-size]'),
      buttons: [].slice.call(el.querySelectorAll('[data-lv-opt-btn], [data-lv-color], [data-lv-size]'))
    };
  });

  function btnValue(b) {
    return b.getAttribute('data-lv-value')
        || b.getAttribute('data-lv-color')
        || b.getAttribute('data-lv-size');
  }

  var nOptions = (product.options || []).length ||
    optBlocks.reduce(function (m, b) { return Math.max(m, b.index + 1); }, 0);

  // Seed selection from the variant Liquid rendered into the form. When size is required
  // (main-product.liquid kza_needs_choice) the form holds NO id: each option then starts from the
  // chip Liquid marked aria-checked (colour lanes, single values), or null = not chosen yet.
  var initialVariant = null;
  var initialId = hiddenId ? parseInt(hiddenId.value, 10) : 0;
  if (initialId) {
    initialVariant = product.variants.filter(function (v) { return v.id === initialId; })[0];
  }

  var selection = [];
  var i;
  if (initialVariant || !optBlocks.length) {
    if (!initialVariant) initialVariant = product.variants[0];
    for (i = 0; i < nOptions; i++) {
      selection[i] = sval(initialVariant['option' + (i + 1)]);
    }
  } else {
    for (i = 0; i < nOptions; i++) selection[i] = null;
    optBlocks.forEach(function (blk) {
      var on = blk.buttons.filter(function (b) { return b.getAttribute('aria-checked') === 'true'; })[0];
      selection[blk.index] = on ? sval(btnValue(on)) : null;
    });
  }

  function selectionComplete() {
    for (var k = 0; k < nOptions; k++) if (selection[k] === null) return false;
    return true;
  }

  // The chips and each option's label always show what the form will add. Liquid renders no size
  // chip checked when a size is required, yet the hidden id can arrive filled (?variant=, or a
  // browser restoring form state on Back without bfcache, which the form's autocomplete="off"
  // also blocks), so every block is repainted from the seeded selection.
  function paintSelection() {
    optBlocks.forEach(function (blk) {
      var sel = selection[blk.index];
      blk.buttons.forEach(function (b) {
        b.setAttribute('aria-checked', sel !== null && sval(btnValue(b)) === sel ? 'true' : 'false');
      });
      if (blk.label) blk.label.textContent = sel === null ? '' : sel;
    });
  }
  paintSelection();

  function moneyShort(cents) {
    if (typeof cents !== 'number') cents = parseInt(cents, 10) || 0;
    var dollars = cents / 100;
    return '$' + (dollars % 1 === 0 ? dollars.toFixed(0) : dollars.toFixed(2));
  }

  function findVariant() {
    return product.variants.filter(function (v) {
      for (var i = 0; i < nOptions; i++) {
        if (selection[i] === null) continue;
        if (sval(v['option' + (i + 1)]) !== selection[i]) return false;
      }
      return true;
    })[0];
  }

  /* `noMatch` distinguishes "this combination does not exist as a variant" from
   * "this variant exists and is sold out". Reporting a nonexistent combination as
   * Sold Out is what made the BERLANDS bug read as a stock problem. */
  function updateStockMessage(v, noMatch) {
    if (!stockEl) return;
    stockEl.className = 'lv-stock-message';
    if (noMatch) {
      stockEl.textContent = 'Not available in this combination';
      stockEl.classList.add('lv-stock-soldout');
      stockEl.removeAttribute('hidden');
      return;
    }
    if (!v || !v.available) {
      stockEl.textContent = 'Sold Out';
      stockEl.classList.add('lv-stock-soldout');
      stockEl.removeAttribute('hidden');
      return;
    }
    if (v.inventory_management && typeof v.inventory_quantity === 'number'
        && v.inventory_quantity > 0 && v.inventory_quantity <= LOW_STOCK_THRESHOLD) {
      stockEl.textContent = 'Almost gone · ' + v.inventory_quantity + ' left in this size';
      stockEl.classList.add('lv-stock-low');
      stockEl.removeAttribute('hidden');
      return;
    }
    stockEl.setAttribute('hidden', '');
    stockEl.textContent = '';
  }

  function updateUiForVariant(v) {
    // Every option is chosen now (updateUiForIncomplete owns the not-chosen state).
    if (pdpForm) pdpForm.removeAttribute('data-kza-needs-size');
    if (atcBtn) atcBtn.removeAttribute('data-kza-size-pending');
    if (!v) {
      // No variant for this combination. Empty AND disable the hidden id so a
      // keyboard/no-JS submit cannot post the previously selected variant --
      // the old code left a stale id in the form.
      if (hiddenId) { hiddenId.value = ''; hiddenId.setAttribute('disabled', ''); }
      if (atcBtn) { atcBtn.setAttribute('disabled', ''); }
      if (atcLabel) atcLabel.textContent = 'Unavailable';
      updateStockMessage(null, true);
      return;
    }
    if (hiddenId) { hiddenId.value = v.id; hiddenId.removeAttribute('disabled'); }
    if (priceEl) priceEl.textContent = moneyShort(v.price);
    if (atcBtn) {
      if (v.available) atcBtn.removeAttribute('disabled');
      else atcBtn.setAttribute('disabled', '');
    }
    if (atcLabel) {
      atcLabel.textContent = v.available
        ? ('Add to Bag · ' + moneyShort(v.price))
        : 'Sold Out';
    }
    updateStockMessage(v);
    if (colorGallery) { var cb = optBlocks.filter(function (b) { return b.el.classList.contains('lv-color-block'); })[0]; var sel = cb && cb.buttons.filter(function (b) { return b.getAttribute('aria-checked') === 'true'; })[0]; filterGalleryByColor(sel ? sval(btnValue(sel)) : (v && cb ? v['option' + (cb.index + 1)] : '')); } else swapImage(v);
  }

  /* Size not chosen yet (spec 2026-09-27 §4.8). The button stays live -- a tap scrolls to the
   * picker (click guard below) -- but the form carries no variant id, and the price shown is the
   * lowest, as Liquid rendered it (product.price). Sold out everywhere -> "Sold Out", disabled. */
  function updateUiForIncomplete() {
    if (pdpForm) pdpForm.setAttribute('data-kza-needs-size', '');
    if (hiddenId) { hiddenId.value = ''; hiddenId.setAttribute('disabled', ''); }
    var anyAvail = product.variants.some(function (v) { return v.available; });
    var minPrice = product.variants.reduce(function (m, v) { return Math.min(m, v.price); }, Infinity);
    if (priceEl && isFinite(minPrice)) priceEl.textContent = moneyShort(minPrice);
    if (atcBtn) {
      atcBtn.removeAttribute('data-kza-size-pending');
      if (anyAvail) atcBtn.removeAttribute('disabled');
      else atcBtn.setAttribute('disabled', '');
    }
    if (atcLabel) atcLabel.textContent = anyAvail ? ('Add to Bag · ' + moneyShort(minPrice)) : 'Sold Out';
    if (stockEl) { stockEl.className = 'lv-stock-message'; stockEl.setAttribute('hidden', ''); stockEl.textContent = ''; }
  }

  // Show the variant's own photo on the lead slide, if it has one.
  function swapImage(v) {
    if (v && v.featured_image && v.featured_image.src) {
      var src = v.featured_image.src;
      // CR-01 fix: build the width-transform URL the same way the preloader below does.
      // Shopify featured_image.src usually carries ?v=<bust>, but if it ever doesn't,
      // appending "&width=1200" with no preceding "?" produces a malformed URL.
      // Bug #8 fix: use the modern ?width= CDN pattern for srcset too (the previous
      // _WIDTHx.ext form is legacy and may stop being supported).
      var srcset = [480, 800, 1200, 1600].map(function (w) {
        return src + (src.indexOf('?') === -1 ? '?width=' + w : '&width=' + w) + ' ' + w + 'w';
      }).join(', ');
      setMainImage(src + (src.indexOf('?') === -1 ? '?width=1200' : '&width=1200'), srcset);
      // The zoom dialog reads data-zoom-src; keep it on the image now showing.
      if (mainImg) mainImg.setAttribute('data-zoom-src', src + (src.indexOf('?') === -1 ? '?width=1600' : '&width=1600'));
    }
  }

  /* Availability, recomputed per option index.
   *
   * A value at option i is offerable if SOME variant carries that value, agrees with
   * every OTHER currently-selected option, and is available. Two states are reported,
   * because they are not the same thing and the accessible name must not claim they are:
   *   - unavailable in EVERY combination      -> "(sold out)"
   *   - only unavailable with the current pick -> "(unavailable with current selection)"
   * The old code updated the class and data-available but never the aria-label, so a
   * screen reader kept announcing a stale "(sold out)" after the selection changed.
   * Struck values stay clickable (no pointer-events:none in lv-base.css), so any dead
   * end recovers in one click. */
  function refreshOptionAvailability() {
    optBlocks.forEach(function (blk) {
      var i = blk.index;
      blk.buttons.forEach(function (b) {
        var val = sval(btnValue(b));
        var withCurrent = product.variants.some(function (v) {
          if (sval(v['option' + (i + 1)]) !== val) return false;
          for (var j = 0; j < nOptions; j++) {
            if (j === i || selection[j] === null) continue;
            if (sval(v['option' + (j + 1)]) !== selection[j]) return false;
          }
          return v.available;
        });
        var everAnywhere = product.variants.some(function (v) {
          return sval(v['option' + (i + 1)]) === val && v.available;
        });
        b.classList.toggle('lv-opt-soldout', !withCurrent);
        b.setAttribute('data-available', withCurrent ? 'true' : 'false');

        var name = (product.options && product.options[i]) || '';
        var suffix = withCurrent ? ''
          : (everAnywhere ? ' (unavailable with current selection)' : ' (sold out)');
        b.setAttribute('aria-label', (name ? name + ' ' : '') + val + suffix);
      });
    });
  }

  optBlocks.forEach(function (blk) {
    blk.buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var val = sval(btnValue(btn));
        selection[blk.index] = val;
        if (blk.label) blk.label.textContent = val;   // block-scoped: no cross-block clobber
        blk.buttons.forEach(function (b) {
          b.setAttribute('aria-checked', sval(btnValue(b)) === val ? 'true' : 'false');
        });
        refreshOptionAvailability();
        if (selectionComplete()) {
          updateUiForVariant(findVariant());
        } else {
          updateUiForIncomplete();
          // A colour picked before the size still shows that colour's photo.
          if (blk.el.classList.contains('lv-color-block')) { if (colorGallery) filterGalleryByColor(sval(btnValue(btn))); else swapImage(findVariant()); }
        }
      });
    });
  });

  // No size chosen: the tap scrolls to the picker and marks it. preventDefault on the CLICK means
  // the form never submits, so the drawer's submit handler never runs: no /cart/add call, no
  // optimistic line, no toast. (The form has no `required` fields, so no validation bubble.)
  if (atcBtn) atcBtn.addEventListener('click', function (e) {
    if (!pdpForm || !pdpForm.hasAttribute('data-kza-needs-size')) return;
    e.preventDefault();
    nudgePicker();
  });

  // Seed availability state on first render
  refreshOptionAvailability();

  // Seed UI on initial render (in case Liquid defaults didn't already set things)
  if (selectionComplete()) updateUiForVariant(findVariant() || initialVariant);
  else updateUiForIncomplete();
  // The sticky bar first synced before this seed; a ?variant= priced unlike product.price
  // would otherwise show the lowest price there until the next chip tap.
  if (typeof syncSticky === 'function') syncSticky();

  // Preload variant featured images so the first color switch is instant (no network).
  // (The SS26.2 gallery renders every product image as a slide, so there are no thumbnails
  // to warm any more; off-screen slides lazy-load on their own.)
  (function preloadAllImages() {
    var seen = {};
    function bump(src) {
      if (!src || seen[src]) return;
      seen[src] = true;
      var pre = new Image();
      pre.decoding = 'async';
      pre.src = src;
    }
    product.variants.forEach(function (v) {
      if (v.featured_image && v.featured_image.src) {
        var s = v.featured_image.src;
        bump(s + (s.indexOf('?') === -1 ? '?width=1200' : '&width=1200'));
      }
    });
  })();
})();
